/**
 * Editable state, wire conversion and client-side validation for an action's
 * custom fields (Conversation Actions). The rules mirror the wizard backend
 * (utils/custom-fields.ts) and the toolbar admin API, so a list that passes
 * here is accepted upstream; the server remains the authoritative check.
 */
import {
  CUSTOM_FIELD_ATTRIBUTE_KEY_MAX_LENGTH,
  CUSTOM_FIELD_KEY_RE,
  CUSTOM_FIELD_LABEL_MAX_LENGTH,
  CUSTOM_FIELD_MAX_LENGTH_DEFAULT,
  CUSTOM_FIELD_MAX_LENGTH_MAX,
  CUSTOM_FIELD_MAX_LENGTH_MIN,
  CUSTOM_FIELD_OPTIONS_MAX,
  CUSTOM_FIELD_OPTIONS_MIN,
  CUSTOM_FIELD_PLACEHOLDER_MAX_LENGTH,
  CUSTOM_FIELDS_MAX_TOTAL,
  CUSTOM_FIELDS_MAX_VISIBLE,
  type CustomField,
  type CustomFieldMode,
  type CustomFieldType,
} from './wizard-backend';

/** Where a field's value comes from: typed here by the admin, or the conversation's participant data. */
export type CustomFieldValueSource = 'admin' | 'participantData';

export interface EditableCustomFieldOption {
  rowKey: string;
  key: string;
  label: string;
  /** Once the admin edits the key, stop auto-slugging from the label. */
  keyEdited: boolean;
}

/** One custom field as edited in the card. All inputs are kept as typed. */
export interface EditableCustomField {
  /** Stable React key; never sent. */
  rowKey: string;
  key: string;
  label: string;
  mode: CustomFieldMode;
  type: CustomFieldType;
  value: string;
  required: boolean;
  placeholder: string;
  /** NaN while the number input is empty. */
  maxLength: number;
  options: EditableCustomFieldOption[];
  /**
   * Text only; a dropdown always sends `admin`. Empty on a new row until the
   * admin picks one (UI-only: saved fields load with their source, and an
   * empty choice blocks the save).
   */
  source: CustomFieldValueSource | '';
  attributeKey: string;
  /** UI-only: added with Add field and not yet confirmed with Save field; never sent. */
  unsaved?: boolean;
}

export type CustomFieldInput = 'key' | 'label' | 'value' | 'placeholder' | 'maxLength' | 'type' | 'options' | 'source' | 'attributeKey';

/** True when the field reads its value from participant data (text only). */
export function isParticipantDataField(field: Pick<EditableCustomField, 'type' | 'source'>): boolean {
  return field.type === 'text' && field.source === 'participantData';
}

/** Per-row problems (i18n keys by input) plus a list-level problem, if any. */
export interface CustomFieldsValidation {
  rows: Array<Partial<Record<CustomFieldInput, string>>>;
  optionRows: Array<Array<Partial<Record<'key' | 'label', string>>>>;
  block: string | null;
  hasErrors: boolean;
}

let nextRowKey = 0;
function newRowKey(): string {
  nextRowKey += 1;
  return `custom-field-${nextRowKey}`;
}

/** Lowercase slug from a label; empty when the label has no Latin letters or digits. */
export function slugFromLabel(label: string): string {
  return label
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 32);
}

export function uniqueOptionKey(base: string, options: ReadonlyArray<{ key: string }>, selfIndex: number): string {
  if (!CUSTOM_FIELD_KEY_RE.test(base)) return base;
  let candidate = base;
  let n = 2;
  const taken = (key: string) => options.some((o, i) => i !== selfIndex && o.key.trim() === key);
  while (taken(candidate)) {
    const suffix = `_${n}`;
    candidate = `${base.slice(0, Math.max(1, 32 - suffix.length))}${suffix}`;
    n += 1;
  }
  return candidate;
}

export function emptyCustomFieldOption(): EditableCustomFieldOption {
  return { rowKey: newRowKey(), key: '', label: '', keyEdited: false };
}

function twoEmptyOptions(): EditableCustomFieldOption[] {
  return [emptyCustomFieldOption(), emptyCustomFieldOption()];
}

export function toEditableCustomField(field: CustomField): EditableCustomField {
  const type: CustomFieldType = field.type === 'dropdown' ? 'dropdown' : 'text';
  const options = type === 'dropdown' && Array.isArray(field.options) && field.options.length > 0
    ? field.options.map((o) => ({
      rowKey: newRowKey(),
      key: o.key,
      label: o.label,
      keyEdited: true,
    }))
    : type === 'dropdown' ? twoEmptyOptions() : [];
  return {
    rowKey: newRowKey(),
    key: field.key,
    label: field.label,
    mode: field.mode,
    type,
    value: field.value ?? '',
    required: field.required === true,
    placeholder: field.placeholder ?? '',
    maxLength: typeof field.maxLength === 'number' ? field.maxLength : CUSTOM_FIELD_MAX_LENGTH_DEFAULT,
    options,
    source: field.source === 'participantData' && type === 'text' ? 'participantData' : 'admin',
    attributeKey: field.attributeKey ?? '',
  };
}

/** Counts agent + fixed fields, the ones agents see. */
export function countVisibleCustomFields(fields: ReadonlyArray<{ mode: CustomFieldMode }>): number {
  return fields.filter((f) => f.mode !== 'hidden').length;
}

/**
 * A new row. Defaults to `agent` + text; once the visible cap is reached the only
 * mode that still fits is `hidden`, so start there instead of on an error. The
 * value source starts unchosen so the admin picks it deliberately.
 */
export function emptyCustomField(existing: ReadonlyArray<EditableCustomField>): EditableCustomField {
  return {
    rowKey: newRowKey(),
    key: '',
    label: '',
    mode: countVisibleCustomFields(existing) >= CUSTOM_FIELDS_MAX_VISIBLE ? 'hidden' : 'agent',
    type: 'text',
    value: '',
    required: false,
    placeholder: '',
    maxLength: CUSTOM_FIELD_MAX_LENGTH_DEFAULT,
    options: [],
    source: '',
    attributeKey: '',
  };
}

/** Exact upstream shape: trimmed, mode-irrelevant properties dropped. */
export function toWireCustomField(field: EditableCustomField): CustomField {
  const isDropdown = field.type === 'dropdown';
  const wire: CustomField = {
    key: field.key.trim(),
    label: field.label.trim(),
    mode: field.mode,
    maxLength: isDropdown ? CUSTOM_FIELD_MAX_LENGTH_DEFAULT : field.maxLength,
  };
  const value = field.value.trim();
  const sourced = isParticipantDataField(field);
  if (field.mode === 'agent') {
    if (value) wire.value = value;
    if (field.required) wire.required = true;
    if (!isDropdown) {
      const placeholder = field.placeholder.trim();
      if (placeholder) wire.placeholder = placeholder;
    }
  } else if (sourced) {
    if (value) wire.value = value;
    if (field.required) wire.required = true;
  } else {
    wire.value = value;
  }
  if (sourced) {
    wire.source = 'participantData';
    wire.attributeKey = field.attributeKey.trim();
  }
  if (isDropdown) {
    wire.type = 'dropdown';
    // Every row is sent, so option counts (and server error indices) match what
    // validateCustomFields() checked.
    wire.options = field.options.map((o) => ({ key: o.key.trim(), label: o.label.trim() }));
  }
  return wire;
}

function isValidMaxLength(n: number): boolean {
  return Number.isInteger(n) && n >= CUSTOM_FIELD_MAX_LENGTH_MIN && n <= CUSTOM_FIELD_MAX_LENGTH_MAX;
}

export function validateCustomFields(fields: ReadonlyArray<EditableCustomField>): CustomFieldsValidation {
  const keyCounts = new Map<string, number>();
  for (const f of fields) {
    const key = f.key.trim();
    if (key) keyCounts.set(key, (keyCounts.get(key) ?? 0) + 1);
  }

  let hasErrors = false;
  const optionRows: Array<Array<Partial<Record<'key' | 'label', string>>>> = [];
  const rows = fields.map((f) => {
    const row: Partial<Record<CustomFieldInput, string>> = {};
    const key = f.key.trim();
    if (!CUSTOM_FIELD_KEY_RE.test(key)) {
      row.key = 'settings-actions-fields-error-key';
    } else if ((keyCounts.get(key) ?? 0) > 1) {
      row.key = 'settings-actions-fields-error-key-duplicate';
    }
    const label = f.label.trim();
    if (!label || label.length > CUSTOM_FIELD_LABEL_MAX_LENGTH) {
      row.label = 'settings-actions-fields-error-label';
    }
    const isDropdown = f.type === 'dropdown';
    if (!isDropdown && !isValidMaxLength(f.maxLength)) {
      row.maxLength = 'settings-actions-fields-error-max-length';
    }
    const value = f.value.trim();
    const optionKeys = isDropdown
      ? f.options.map((o) => o.key.trim()).filter((k) => CUSTOM_FIELD_KEY_RE.test(k))
      : [];
    const sourced = isParticipantDataField(f);
    if (!f.source) {
      row.source = 'settings-actions-fields-error-source';
    }
    if (sourced) {
      const attributeKey = f.attributeKey.trim();
      if (!attributeKey || attributeKey.length > CUSTOM_FIELD_ATTRIBUTE_KEY_MAX_LENGTH) {
        row.attributeKey = 'settings-actions-fields-error-attribute-key';
      }
    }
    if (f.mode !== 'agent' && !value && !sourced) {
      row.value = isDropdown
        ? 'settings-actions-fields-error-value-option'
        : 'settings-actions-fields-error-value-required';
    } else if (isDropdown && value && !optionKeys.includes(value)) {
      row.value = 'settings-actions-fields-error-value-option';
    } else if (!isDropdown && isValidMaxLength(f.maxLength) && value.length > f.maxLength) {
      row.value = 'settings-actions-fields-error-value-length';
    }
    if (!isDropdown && f.mode === 'agent' && f.placeholder.trim().length > CUSTOM_FIELD_PLACEHOLDER_MAX_LENGTH) {
      row.placeholder = 'settings-actions-fields-error-placeholder';
    }

    const optErrors: Array<Partial<Record<'key' | 'label', string>>> = [];
    if (isDropdown) {
      if (f.options.length < CUSTOM_FIELD_OPTIONS_MIN) {
        row.options = 'settings-actions-fields-error-options-min';
      } else if (f.options.length > CUSTOM_FIELD_OPTIONS_MAX) {
        row.options = 'settings-actions-fields-error-options-max';
      }
      const optKeyCounts = new Map<string, number>();
      // Labels compare case-insensitively (server rule): agents cannot tell two same-reading options apart.
      const optLabelCounts = new Map<string, number>();
      for (const o of f.options) {
        const ok = o.key.trim();
        if (ok) optKeyCounts.set(ok, (optKeyCounts.get(ok) ?? 0) + 1);
        const ol = o.label.trim().toLowerCase();
        if (ol) optLabelCounts.set(ol, (optLabelCounts.get(ol) ?? 0) + 1);
      }
      for (const o of f.options) {
        const opt: Partial<Record<'key' | 'label', string>> = {};
        const ok = o.key.trim();
        if (!CUSTOM_FIELD_KEY_RE.test(ok)) {
          opt.key = 'settings-actions-fields-error-option-key';
        } else if ((optKeyCounts.get(ok) ?? 0) > 1) {
          opt.key = 'settings-actions-fields-error-option-key-duplicate';
        }
        const ol = o.label.trim();
        if (!ol || ol.length > CUSTOM_FIELD_LABEL_MAX_LENGTH) {
          opt.label = 'settings-actions-fields-error-option-label';
        } else if ((optLabelCounts.get(ol.toLowerCase()) ?? 0) > 1) {
          opt.label = 'settings-actions-fields-error-option-label-duplicate';
        }
        if (Object.keys(opt).length > 0) hasErrors = true;
        optErrors.push(opt);
      }
    }
    optionRows.push(optErrors);
    if (Object.keys(row).length > 0) hasErrors = true;
    return row;
  });

  let block: string | null = null;
  if (fields.length > CUSTOM_FIELDS_MAX_TOTAL) {
    block = 'settings-actions-fields-error-total-cap';
  } else if (countVisibleCustomFields(fields) > CUSTOM_FIELDS_MAX_VISIBLE) {
    block = 'settings-actions-fields-error-visible-cap';
  }

  return { rows, optionRows, block, hasErrors: hasErrors || block !== null };
}

/** Sample text for the payload viewer: configured value, or a stand-in / first option key. */
export function sampleCustomFieldValues(fields: ReadonlyArray<EditableCustomField>): Record<string, string> {
  const sample: Record<string, string> = {};
  for (const f of fields) {
    const key = f.key.trim();
    if (!key) continue;
    if (f.type === 'dropdown') {
      const optionKey = f.value.trim()
        || f.options.map((o) => o.key.trim()).find((k) => CUSTOM_FIELD_KEY_RE.test(k))
        || '';
      if (optionKey) sample[key] = optionKey;
    } else {
      sample[key] = f.value.trim() || 'Sample text';
    }
  }
  return sample;
}
